import { useState } from 'react';
import { entranceAudio } from '@/lib/entranceAudio';

// Premium Brotherhood+ entrance — a cinematic, masculine "members-club door"
// shown ONCE before the existing splash. Purely presentational: it calls
// onEnter() when the member taps ENTER, then unmounts so the existing splash
// plays untouched. No audio (soundtrack pending), no routing/auth changes.
export default function BrandEntrance({ onEnter, onExited }) {
  const [leaving, setLeaving] = useState(false);

  const handleEnter = () => {
    if (leaving) return;
    setLeaving(true);
    entranceAudio.start();              // start signature music on this user gesture
    onEnter && onEnter();               // mount splash behind immediately (no landing flash)
    setTimeout(() => onExited && onExited(), 620); // remove entrance after fade
  };

  return (
    <div
      data-testid="brand-entrance"
      className={`be-root ${leaving ? 'be-leaving' : ''}`}
      role="dialog"
      aria-label="Enter Brotherhood+"
    >
      <style>{BE_CSS}</style>

      {/* Cinematic animated background layers */}
      <div className="be-bg" />
      <div className="be-rays" />
      <div className="be-mist be-mist-1" />
      <div className="be-mist be-mist-2" />
      <div className="be-vignette" />
      <div className="be-grain" />
      <div className="be-particles">
        {Array.from({ length: 14 }).map((_, i) => (
          <span key={i} className={`be-dot be-dot-${(i % 7) + 1}`} />
        ))}
      </div>

      {/* Content */}
      <div className="be-content">
        <div className="be-logo be-reveal be-reveal-1">
          <span className="be-word">Brotherhood<span className="be-plus">+</span></span>
        </div>

        <p className="be-tagline be-reveal be-reveal-2">STRENGTH. PURPOSE. BROTHERHOOD.</p>

        <button
          type="button"
          data-testid="brand-entrance-enter"
          onClick={handleEnter}
          className="be-enter be-reveal be-reveal-3"
          aria-label="Enter"
        >
          <span className="be-enter-glow" />
          <span className="be-enter-label">ENTER</span>
        </button>
      </div>
    </div>
  );
}

const BE_CSS = `
.be-root{position:fixed;inset:0;z-index:1000;overflow:hidden;background:#0B192C;
  display:flex;align-items:center;justify-content:center;
  opacity:1;transition:opacity .6s ease;}
.be-root.be-leaving{opacity:0;pointer-events:none;}
.be-root *{box-sizing:border-box;}

.be-bg{position:absolute;inset:-10%;
  background:
    radial-gradient(120% 80% at 80% 0%, rgba(197,160,89,0.10), transparent 55%),
    radial-gradient(120% 90% at 10% 100%, rgba(7,16,30,0.85), transparent 60%),
    linear-gradient(155deg, #0B192C 0%, #0C1D34 40%, #0A1728 62%, #0B192C 100%);
  animation:beDrift 22s ease-in-out infinite alternate;}
@keyframes beDrift{0%{transform:scale(1.05) translate3d(0,0,0);}100%{transform:scale(1.12) translate3d(-1.5%,-1.5%,0);}}

.be-rays{position:absolute;inset:-40%;pointer-events:none;mix-blend-mode:screen;opacity:.5;
  background:conic-gradient(from 200deg at 70% 20%,
    transparent 0deg, rgba(197,160,89,0.10) 18deg, transparent 40deg,
    transparent 160deg, rgba(120,140,170,0.08) 182deg, transparent 210deg);
  animation:beRays 40s linear infinite;}
@keyframes beRays{to{transform:rotate(360deg);}}

.be-mist{position:absolute;border-radius:50%;filter:blur(60px);pointer-events:none;opacity:.5;}
.be-mist-1{width:60vw;height:60vw;left:-10vw;top:-15vw;
  background:radial-gradient(circle, rgba(90,110,138,0.22), transparent 70%);
  animation:beMist1 26s ease-in-out infinite alternate;}
.be-mist-2{width:55vw;height:55vw;right:-12vw;bottom:-18vw;
  background:radial-gradient(circle, rgba(176,141,87,0.18), transparent 70%);
  animation:beMist2 30s ease-in-out infinite alternate;}
@keyframes beMist1{0%{transform:translate3d(0,0,0);}100%{transform:translate3d(6vw,4vw,0);}}
@keyframes beMist2{0%{transform:translate3d(0,0,0);}100%{transform:translate3d(-5vw,-3vw,0);}}

.be-vignette{position:absolute;inset:0;pointer-events:none;
  background:radial-gradient(120% 120% at 50% 45%, transparent 45%, rgba(0,0,0,0.65) 100%);}
.be-grain{position:absolute;inset:0;pointer-events:none;opacity:.05;mix-blend-mode:overlay;
  background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");}

.be-particles{position:absolute;inset:0;pointer-events:none;}
.be-dot{position:absolute;bottom:-6px;width:3px;height:3px;border-radius:50%;
  background:rgba(197,160,89,0.55);box-shadow:0 0 6px rgba(197,160,89,0.5);
  animation:beRise linear infinite;}
@keyframes beRise{0%{transform:translateY(0);opacity:0;}
  12%{opacity:.9;}88%{opacity:.5;}100%{transform:translateY(-105vh);opacity:0;}}
.be-dot-1{left:12%;animation-duration:15s;animation-delay:0s;}
.be-dot-2{left:24%;animation-duration:19s;animation-delay:3s;width:2px;height:2px;}
.be-dot-3{left:38%;animation-duration:13s;animation-delay:6s;}
.be-dot-4{left:52%;animation-duration:21s;animation-delay:1.5s;width:2px;height:2px;}
.be-dot-5{left:66%;animation-duration:17s;animation-delay:4.5s;}
.be-dot-6{left:78%;animation-duration:23s;animation-delay:8s;width:2px;height:2px;}
.be-dot-7{left:90%;animation-duration:16s;animation-delay:2.5s;}

.be-content{position:relative;z-index:2;display:flex;flex-direction:column;align-items:center;
  padding:24px;text-align:center;width:100%;max-width:640px;}
.be-logo{display:flex;align-items:center;gap:14px;}
.be-badge{width:64px;height:64px;border-radius:20px;display:flex;align-items:center;justify-content:center;
  font-family:'Playfair Display',Georgia,serif;font-weight:700;font-size:30px;color:#F5F1E8;
  background:linear-gradient(160deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02));
  border:1px solid rgba(197,160,89,0.45);
  box-shadow:0 8px 30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08);
  backdrop-filter:blur(6px);}
.be-word{font-family:'Playfair Display',Georgia,serif;font-weight:700;font-size:40px;color:#F5F1E8;
  letter-spacing:0.5px;}
.be-plus{color:#C5A059;}

.be-tagline{margin-top:26px;color:rgba(245,241,232,0.72);font-size:13px;font-weight:600;
  letter-spacing:0.42em;text-transform:uppercase;
  font-family:ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif;}

.be-enter{position:relative;margin-top:52px;cursor:pointer;border:none;outline:none;
  padding:20px 64px;border-radius:999px;overflow:hidden;
  background:linear-gradient(160deg, rgba(18,35,58,0.85), rgba(9,20,36,0.9));
  border:1.5px solid transparent;
  background-clip:padding-box;
  box-shadow:0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(197,160,89,0.35),
    inset 0 1px 0 rgba(255,255,255,0.10);
  backdrop-filter:blur(10px);
  transition:transform .35s cubic-bezier(.2,.8,.2,1), box-shadow .35s ease;}
.be-enter::before{content:"";position:absolute;inset:0;border-radius:999px;padding:1.5px;
  background:linear-gradient(150deg, rgba(197,160,89,0.9), rgba(176,141,87,0.25) 45%, rgba(197,160,89,0.8));
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  -webkit-mask-composite:xor;mask-composite:exclude;pointer-events:none;}
.be-enter:hover{transform:translateY(-3px);
  box-shadow:0 18px 52px rgba(0,0,0,0.6), 0 0 26px rgba(197,160,89,0.35),
    inset 0 1px 0 rgba(255,255,255,0.14);}
.be-enter:active{transform:translateY(-1px) scale(.985);}
.be-enter-label{position:relative;z-index:2;color:#F5F1E8;font-weight:700;font-size:16px;
  letter-spacing:0.34em;font-family:ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif;}
.be-enter-glow{position:absolute;inset:0;border-radius:999px;pointer-events:none;
  box-shadow:0 0 0 rgba(197,160,89,0);animation:beGlow 4.5s ease-in-out infinite;}
@keyframes beGlow{0%,100%{box-shadow:0 0 0 rgba(197,160,89,0);}
  50%{box-shadow:0 0 34px rgba(197,160,89,0.30);}}

.be-reveal{opacity:0;transform:translateY(16px);animation:beReveal .95s cubic-bezier(.2,.8,.2,1) forwards;}
.be-reveal-1{animation-delay:.15s;}
.be-reveal-2{animation-delay:.6s;}
.be-reveal-3{animation-delay:1.05s;transform:translateY(26px);}
@keyframes beReveal{to{opacity:1;transform:translateY(0);}}

@media (max-width:640px){
  .be-badge{width:52px;height:52px;border-radius:16px;font-size:24px;}
  .be-word{font-size:30px;}
  .be-tagline{font-size:11px;letter-spacing:0.34em;margin-top:22px;}
  .be-enter{padding:18px 52px;margin-top:44px;}
}
@media (prefers-reduced-motion:reduce){
  .be-bg,.be-rays,.be-mist,.be-dot,.be-enter-glow{animation:none;}
  .be-reveal{animation-duration:.5s;}
}
`;
